import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Restaurant, SortBy } from '@/types';
import type { Language } from '@/config/locales';
import type { TranslationStrings } from '@/i18n/translations';
import { getRestaurantPath } from '@/config/routes';
import { isValidTableContext, TABLE_SESSION_CHANGED_EVENT } from '@/lib/tableContext';
import { isViewOnlyMode } from '@/lib/app-utils';
import { useAllStores, createApiStoreTranslations } from './useAllStores';

interface UseRestaurantsOptions {
  language: Language;
  translations: TranslationStrings;
}

// API 매장에 추가되는 확장 필드
interface ApiRestaurant extends Restaurant {
  _name?: string;
  _cuisine?: string;
}

// localStorage 키
const FAVORITES_STORAGE_KEY = 'olulo_favorite_restaurants';

// 즐겨찾기 목록 로드
function loadFavorites(): string[] {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // 의도적으로 에러 무시: localStorage 접근 실패 시 빈 배열 반환 (즐겨찾기 기능)
  }
  return [];
}

// 즐겨찾기 목록 저장
function saveFavorites(favorites: string[]): void {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
  } catch {
    // 의도적으로 에러 무시: localStorage 저장 실패 시 즐겨찾기만 초기화됨 (다시 열면 기본 상태)
  }
}

function getDistanceSortValue(distance: string): number {
  const match = distance.trim().match(/^(\d+(?:\.\d+)?)\s*km$/i);
  if (!match) {
    return Number.POSITIVE_INFINITY;
  }

  const value = Number.parseFloat(match[1]);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

export function useRestaurants({ language, translations }: UseRestaurantsOptions) {
  // URL 라우팅 훅
  const navigate = useNavigate();
  const location = useLocation();

  // 매장 목록이 필요한 경로인지 확인
  // - / (홈): 매장 검색/필터
  // - /restaurant/*: 메뉴 페이지
  // - /orders/*: 주문 목록/상세 (매장명 표시)
  // 그 외 경로 (/organizations, /org/*, /login, /profile 등)는 불필요
  const shouldFetchStores =
    location.pathname === '/' ||
    location.pathname.startsWith('/restaurant') ||
    location.pathname.startsWith('/orders');

  // API 데이터 훅 (enabled 옵션으로 불필요한 페이지에서 API/Geolocation 호출 방지)
  const {
    restaurants: apiRestaurants,
    loading: apiLoading,
    error: apiError,
    isFallbackToAll,
  } = useAllStores({ enabled: shouldFetchStores });

  // 상태 (URL과 독립적인 상태만)
  const [favorites, setFavorites] = useState<string[]>(() => loadFavorites());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // 테이블 세션 토큰 변경(만료 clear 등)에 showTableOrderMenu 가 반응하도록 버전 카운터.
  // sessionStorage 는 같은 탭에서 반응형이 아니므로 tableContext 의 명시 이벤트를 구독한다.
  const [tableSessionVersion, setTableSessionVersion] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = () => setTableSessionVersion(v => v + 1);
    window.addEventListener(TABLE_SESSION_CHANGED_EVENT, handle);
    return () => window.removeEventListener(TABLE_SESSION_CHANGED_EVENT, handle);
  }, []);

  // 이전 데이터 캐시 (로딩 중 데이터 유지용)
  const prevRestaurantsRef = useRef<Restaurant[]>([]);

  // API 매장용 동적 번역 생성
  const apiTranslations = useMemo(() => {
    return createApiStoreTranslations(apiRestaurants as ApiRestaurant[]);
  }, [apiRestaurants]);

  // 통합 번역 (기존 번역 + API 매장 번역)
  const mergedTranslations = useMemo(() => {
    return {
      ...translations,
      restaurants: {
        ...translations.restaurants,
        ...apiTranslations,
      },
    };
  }, [translations, apiTranslations]);

  // 레스토랑 데이터 (API 데이터만 사용, 목업 데이터 사용 안함)
  const restaurants = useMemo(() => {
    // API 데이터가 있으면 사용 (즐겨찾기 상태 적용)
    if (apiRestaurants.length > 0) {
      const result = apiRestaurants.map((r) => ({
        ...r,
        isFavorite: favorites.includes(r.id),
      }));
      // 캐시 업데이트
      prevRestaurantsRef.current = result;
      return result;
    }

    // API 로딩 중이고 이전 데이터가 있으면 이전 데이터 유지
    // (위치 변경으로 인한 API 재호출 시 깜빡임 방지)
    if (apiLoading && prevRestaurantsRef.current.length > 0) {
      return prevRestaurantsRef.current;
    }

    // API 결과가 0개 또는 에러인 경우 - 빈 배열 반환
    // 목업 데이터 사용하지 않음
    return [];
  }, [apiRestaurants, favorites, apiLoading]);

  // 근처 매장 없음 상태 (UI에서 메시지 표시용)
  // isFallbackToAll이 true면 이미 전체 매장을 표시 중이므로 false
  const hasNoNearbyStores = useMemo(() => {
    if (isFallbackToAll) return false;
    return !apiLoading && !apiError && apiRestaurants.length === 0;
  }, [apiLoading, apiError, apiRestaurants.length, isFallbackToAll]);

  // URL에서 레스토랑 관련 상태 파생 (단일 진실 원천)
  const { showPickupMenu, showTableOrderMenu, selectedRestaurant } = useMemo(() => {
    const match = location.pathname.match(/^\/restaurant\/([^/]+)/);
    if (!match) {
      return {
        showPickupMenu: false,
        showTableOrderMenu: false,
        selectedRestaurant: null,
      };
    }

    // #434 (P1): ?mode=view(열람 전용 QR, #541)는 이 빠른 경로 대상이 아니다.
    // 근처 매장 리스트에 있는 isOpen 매장이라도 view 진입이면 showPickupMenu=true 로
    // 전환되어 PickupMenuWrapper 가 mode='pickup'(주문 가능 UI)으로 렌더되는 우회가
    // 발생하므로, RestaurantRoute → RestaurantPage(mode='view' 판정 단일 출처)로 위임한다.
    // 판정은 App.tsx/RestaurantPage 와 동일한 isViewOnlyMode 헬퍼 사용 (드리프트 예방).
    const viewModeParam = new URLSearchParams(location.search).get('mode');
    if (isViewOnlyMode(location.pathname, viewModeParam)) {
      return {
        showPickupMenu: false,
        showTableOrderMenu: false,
        selectedRestaurant: null,
      };
    }

    const restaurantId = match[1];
    const isTableOrder = location.pathname.includes('/table/');
    const restaurant = restaurants.find(r => r.id === restaurantId);

    // 레스토랑이 없거나 닫혀있으면 표시하지 않음
    if (!restaurant || !restaurant.isOpen) {
      return {
        showPickupMenu: false,
        showTableOrderMenu: false,
        selectedRestaurant: null,
      };
    }

    // 테이블 경로(/table/)는 유효한 세션 토큰이 있을 때만 이 빠른 경로로 테이블 모드 렌더.
    // 토큰이 없으면(QR 스캔 없이 직접 URL 진입 등) 여기서 빠져 RestaurantRoute →
    // RestaurantPage(토큰 기반 mode + 토큰 부재 시 QR 재스캔 라우팅)로 위임한다.
    //
    // 배경: 근처 매장 리스트에 있는 매장은 (isOpen 만 만족하면) 토큰 없이도
    // showTableOrderMenu=true 가 되어 옛 PickupMenuWrapper 경로가 토큰 검증을
    // 우회하던 불일치를 차단한다. (리스트 밖 매장은 restaurant 미발견으로 이미 위임됨)
    // 박제 D 단일 출처(isValidTableContext): path `/table/` + 토큰 둘 다 만족해야 table.
    const tableSessionToken =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('table_session_token')
        : null;
    if (isTableOrder && !isValidTableContext(location.pathname, tableSessionToken)) {
      return {
        showPickupMenu: false,
        showTableOrderMenu: false,
        selectedRestaurant: null,
      };
    }

    return {
      showPickupMenu: !isTableOrder,
      showTableOrderMenu: isTableOrder,
      selectedRestaurant: restaurant,
    };
    // tableSessionVersion 은 body 에서 직접 참조하진 않지만, 토큰 변경 이벤트 시
    // memo 재계산(토큰 clear → 테이블 모드 해제)을 트리거하는 의도적 invalidation dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, restaurants, tableSessionVersion]);

  // 필터링된 레스토랑
  const filteredRestaurants = useMemo(() => {
    return restaurants
      .filter(restaurant => {
        // API 매장의 경우 _name 사용, 아니면 번역 사용
        const apiRestaurant = restaurant as ApiRestaurant;
        const restaurantName = apiRestaurant._name ||
          mergedTranslations.restaurants[restaurant.nameKey]?.name ||
          restaurant.nameKey;
        const restaurantCuisine = apiRestaurant._cuisine ||
          mergedTranslations.restaurants[restaurant.cuisineKey]?.cuisine || '';

        const matchesSearch = (restaurantName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                             (restaurantCuisine || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || restaurant.category === selectedCategory;
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'rating': return b.rating - a.rating;
          case 'distance': {
            // menu#403 — 단위 표기 'km'/'KM' 모두 대응 (case-insensitive + 공백 허용)
            const aDistance = getDistanceSortValue(a.distance);
            const bDistance = getDistanceSortValue(b.distance);
            if (aDistance === bDistance) return 0;
            return aDistance < bDistance ? -1 : 1;
          }
          case 'name': {
            const aApi = a as ApiRestaurant;
            const bApi = b as ApiRestaurant;
            const aName = aApi._name || mergedTranslations.restaurants[a.nameKey]?.name || a.nameKey;
            const bName = bApi._name || mergedTranslations.restaurants[b.nameKey]?.name || b.nameKey;
            return aName.localeCompare(bName, language === 'ko' ? 'ko' : 'en');
          }
          default: return 0;
        }
      });
  }, [restaurants, searchQuery, selectedCategory, sortBy, mergedTranslations, language]);

  // 지도용 매장 데이터
  const mapStores = useMemo(() => {
    return restaurants
      .filter(r => r.lat && r.lng)
      .map(r => {
        const apiRestaurant = r as ApiRestaurant;
        return {
          id: r.id,
          name: apiRestaurant._name || mergedTranslations.restaurants[r.nameKey]?.name || r.nameKey,
          address: r.address || '',
          lat: r.lat || 37.5665,
          lng: r.lng || 126.9780,
          isOpen: r.isOpen,
          distance: r.distance,
          rating: r.rating,
          imageUrl: r.imageUrl
        };
      });
  }, [restaurants, mergedTranslations]);

  // 카테고리 선택 타이머 ref (cleanup용)
  const categoryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 카테고리 선택 타이머 cleanup
  useEffect(() => {
    return () => {
      if (categoryTimerRef.current) {
        clearTimeout(categoryTimerRef.current);
      }
    };
  }, []);

  // 핸들러
  const handleCategorySelect = useCallback((categoryId: string) => {
    // 이전 타이머 정리
    if (categoryTimerRef.current) {
      clearTimeout(categoryTimerRef.current);
    }
    setIsLoading(true);
    categoryTimerRef.current = setTimeout(() => {
      setSelectedCategory(categoryId);
      setIsLoading(false);
    }, 300);
  }, []);

  const handleFavoriteToggle = useCallback((restaurantId: string) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(restaurantId)
        ? prev.filter(id => id !== restaurantId)
        : [...prev, restaurantId];
      // localStorage에 저장
      saveFavorites(newFavorites);
      return newFavorites;
    });
  }, []);

  const handleRestaurantSelect = useCallback((restaurantId: string) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    if (restaurant && restaurant.isOpen) {
      // URL 변경 - 상태는 useEffect에서 자동 동기화됨
      navigate(getRestaurantPath(restaurantId));
    }
  }, [restaurants, navigate]);

  // 지도에서 매장 선택
  const handleStoreSelect = useCallback((storeId: string) => {
    const restaurant = restaurants.find(r => r.id === storeId);
    if (restaurant && restaurant.isOpen) {
      // URL 변경 - 상태는 useEffect에서 자동 동기화됨
      navigate(getRestaurantPath(storeId));
    }
  }, [restaurants, navigate]);

  const handleBackFromPickupMenu = useCallback(() => {
    // 홈으로 돌아가기 - 상태는 useEffect에서 자동 초기화됨
    navigate('/');
  }, [navigate]);

  const handleBackFromTableOrderMenu = useCallback(() => {
    // 홈으로 돌아가기 - 상태는 useEffect에서 자동 초기화됨
    navigate('/');
  }, [navigate]);

  const handleNavigateToTableOrder = useCallback(() => {
    const firstOpenRestaurant = restaurants.find(r => r.isOpen);
    if (firstOpenRestaurant) {
      // 테이블 주문 URL로 이동 (기본 테이블 번호 1)
      navigate(`/restaurant/${firstOpenRestaurant.id}/table/1`);
    }
  }, [restaurants, navigate]);

  // 레스토랑 상태 초기화 (홈으로 이동)
  const resetRestaurantState = useCallback(() => {
    // 현재 레스토랑 페이지에 있으면 홈으로 이동
    // URL 변경 시 useMemo를 통해 상태가 자동으로 초기화됨
    if (location.pathname.startsWith('/restaurant')) {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  return {
    // URL 기반 상태 (읽기 전용)
    favorites,
    selectedRestaurant, // URL에서 파생
    showPickupMenu, // URL에서 파생
    showTableOrderMenu, // URL에서 파생

    // 로컬 상태
    searchQuery,
    setSearchQuery,
    selectedCategory,
    sortBy,
    setSortBy,
    isLoading: isLoading || apiLoading,

    // 데이터
    restaurants,
    filteredRestaurants,
    mapStores,

    // API 상태
    apiError,
    hasNoNearbyStores,
    isFallbackToAll, // 근처 매장 없어서 전체 매장 표시 중

    // 통합 번역 (API 매장 포함)
    mergedTranslations,

    // 핸들러
    handleCategorySelect,
    handleFavoriteToggle,
    handleRestaurantSelect,
    handleStoreSelect,
    handleBackFromPickupMenu,
    handleBackFromTableOrderMenu,
    handleNavigateToTableOrder,
    resetRestaurantState,
  };
}
